import { ConnectedPosition, Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkdownModule } from 'ngx-markdown';
import { tap } from 'rxjs';
import type { SuperLatexAnnotation } from '../super-latex.model';

@Component({
  selector: 'dojo-annotation-engine',
  standalone: true,
  imports: [MarkdownModule, OverlayModule],
  templateUrl: './annotation-engine.component.html',
  styleUrls: ['./annotation-engine.component.scss'],
  host: { class: 'block' },
})
export class AnnotationEngineComponent implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor() {
    effect(() => {
      const trigger = this.annotation().trigger;
      const target = this.targetElement();

      if (!target || this.hasSetupEventListeners) {
        return;
      }

      target.classList.add('has-annotation');

      if (trigger === 'hover') {
        target.addEventListener('mouseenter', this.handleTargetMouseEnter);
        target.addEventListener('mouseleave', this.handleTargetMouseLeave);
        this.hasSetupEventListeners = true;
      } else if (trigger === 'click') {
        target.style.cursor = 'pointer';
        target.addEventListener('click', this.handleTargetClick);
        this.hasSetupEventListeners = true;
      }
    });

    effect(() => {
      if (this.isVisible()) {
        if (this.annotation().trigger === 'click') {
          setTimeout(() => {
            document.addEventListener('click', this.handleDocumentClick);
            document.addEventListener('keydown', this.handleEscapeKey);
          }, 0);
        } else if (this.annotation().trigger === 'hover') {
          setTimeout(() => {
            if (this.annotationElement) {
              this.annotationElement.addEventListener('mouseenter', this.handleAnnotationMouseEnter);
              this.annotationElement.addEventListener('mouseleave', this.handleAnnotationMouseLeave);
            }
          }, 100);
        }
      } else {
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleEscapeKey);
        if (this.annotationElement) {
          this.annotationElement.removeEventListener('mouseenter', this.handleAnnotationMouseEnter);
          this.annotationElement.removeEventListener('mouseleave', this.handleAnnotationMouseLeave);
        }
      }
    });
  }

  private overlayRef?: OverlayRef;
  private portal?: TemplatePortal;
  private hideTimeout?: ReturnType<typeof setTimeout>;
  private hasSetupEventListeners = false;
  private annotationElement?: HTMLElement;

  readonly annotation = input.required<SuperLatexAnnotation>();
  readonly targetElement = input.required<HTMLElement>();
  readonly overlayTemplate = viewChild.required<TemplateRef<unknown>>('overlayContent');
  readonly isVisible = signal(false);
  readonly isPositioned = signal(false);
  readonly connectorPathData = signal('');
  readonly connectorColor = signal('');

  readonly anchorStyle = computed(() => {
    const annotation = this.annotation();
    const isHighlighted = this.targetElement()?.classList.contains('highlighted');
    return annotation.anchor_style ?? (isHighlighted ? 'circle' : 'arrow');
  });

  readonly elbowAngle = computed(() => this.annotation().elbow_angle ?? 90);
  readonly latexContent = computed(() => `$${this.annotation().content}$`);
  readonly katexOptions = {
    throwOnError: false,
    strict: 'warn' as const,
    trust: true,
  };

  ngOnDestroy(): void {
    this.clearHideTimeout();
    this.destroyOverlay();
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleEscapeKey);

    const target = this.targetElement();
    if (target) {
      target.classList.remove('has-annotation');
      target.style.cursor = '';

      const trigger = this.annotation().trigger;
      if (trigger === 'hover') {
        target.removeEventListener('mouseenter', this.handleTargetMouseEnter);
        target.removeEventListener('mouseleave', this.handleTargetMouseLeave);
      } else if (trigger === 'click') {
        target.removeEventListener('click', this.handleTargetClick);
      }
    }

    if (this.annotationElement) {
      this.annotationElement.removeEventListener('mouseenter', this.handleAnnotationMouseEnter);
      this.annotationElement.removeEventListener('mouseleave', this.handleAnnotationMouseLeave);
    }
  }

  private readonly handleTargetClick = (event: Event): void => {
    event.stopPropagation();
    this.toggle();
  };

  private readonly handleDocumentClick = (event: Event): void => {
    const target = this.targetElement();
    const clickedElement = event.target as Node;
    const clickedInsideAnnotation = this.annotationElement?.contains(clickedElement);
    const clickedInsideTarget = target?.contains(clickedElement);

    if (!clickedInsideAnnotation && !clickedInsideTarget) {
      this.hide();
    }
  };

  private readonly handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.hide();
    }
  };

  private readonly handleTargetMouseEnter = (): void => {
    this.clearHideTimeout();
    void this.show();
  };

  private readonly handleTargetMouseLeave = (): void => {
    this.scheduleHide();
  };

  private readonly handleAnnotationMouseEnter = (): void => {
    this.clearHideTimeout();
  };

  private readonly handleAnnotationMouseLeave = (): void => {
    this.scheduleHide();
  };

  private async show(): Promise<void> {
    if (this.isVisible()) {
      return;
    }

    this.isVisible.set(true);
    this.createOverlay();

    requestAnimationFrame(() => {
      const overlayElement = this.overlayRef?.overlayElement;
      if (overlayElement) {
        this.annotationElement = overlayElement.querySelector('.annotation-box') as HTMLElement;
        this.updateConnectorLine();
        requestAnimationFrame(() => {
          this.isPositioned.set(true);
        });
      }
    });
  }

  private hide(): void {
    this.clearHideTimeout();
    this.isVisible.set(false);
    this.isPositioned.set(false);
    this.destroyOverlay();
  }

  private createOverlay(): void {
    if (this.overlayRef) {
      return;
    }

    const target = this.targetElement();
    const annotation = this.annotation();
    const placement = annotation.placement ?? 'top';
    const distance = annotation.distance ?? 8;

    const positions: ConnectedPosition[] = [
      { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -distance },
      { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: distance },
      { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -distance },
      { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: distance },
    ];

    let preferredPosition: ConnectedPosition;
    switch (placement) {
      case 'bottom':
        preferredPosition = positions[1];
        break;
      case 'left':
        preferredPosition = positions[2];
        break;
      case 'right':
        preferredPosition = positions[3];
        break;
      default:
        preferredPosition = positions[0];
    }

    const orderedPositions = [preferredPosition, ...positions.filter((p) => p !== preferredPosition)];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(target)
      .withPositions(orderedPositions)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withViewportMargin(8);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });

    const template = this.overlayTemplate();
    this.portal = new TemplatePortal(template, this.viewContainerRef);
    this.overlayRef.attach(this.portal);

    positionStrategy.positionChanges
      .pipe(
        tap(() => this.updateConnectorLine()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private destroyOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
    this.portal = undefined;
    this.annotationElement = undefined;
  }

  private updateConnectorLine(): void {
    if (!this.annotationElement) {
      return;
    }

    const target = this.targetElement();
    if (!target) {
      return;
    }

    const annotationRect = this.annotationElement.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    const targetComputedStyle = window.getComputedStyle(target);
    const connectionColor = targetComputedStyle.getPropertyValue('--katex-connection-color').trim() || '#0ea5e9';
    this.connectorColor.set(connectionColor);

    const annotationBg = targetComputedStyle.getPropertyValue('--katex-annotation-bg').trim() || '#f0f9ff';
    const annotationText = targetComputedStyle.getPropertyValue('--katex-annotation-text').trim() || '#0284c7';
    this.annotationElement.style.backgroundColor = annotationBg;
    this.annotationElement.style.color = annotationText;

    const annotationCenterX = annotationRect.left + annotationRect.width / 2;
    const annotationCenterY = annotationRect.top + annotationRect.height / 2;
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    let startX: number;
    let startY: number;
    let endX: number;
    let endY: number;

    if (annotationRect.bottom <= targetRect.top) {
      startX = annotationCenterX;
      startY = annotationRect.bottom;
      endX = targetCenterX;
      endY = targetRect.top;
    } else if (annotationRect.top >= targetRect.bottom) {
      startX = annotationCenterX;
      startY = annotationRect.top;
      endX = targetCenterX;
      endY = targetRect.bottom;
    } else if (annotationRect.right <= targetRect.left) {
      startX = annotationRect.right;
      startY = annotationCenterY;
      endX = targetRect.left;
      endY = targetCenterY;
    } else {
      startX = annotationRect.left;
      startY = annotationCenterY;
      endX = targetRect.right;
      endY = targetCenterY;
    }

    const path = this.calculateElbowPath(startX, startY, endX, endY);
    this.connectorPathData.set(path);
  }

  private calculateElbowPath(startX: number, startY: number, endX: number, endY: number): string {
    const angle = this.elbowAngle();
    const isVertical = Math.abs(endY - startY) > Math.abs(endX - startX);

    if (angle === 90) {
      if (isVertical) {
        const midY = startY + (endY - startY) / 2;
        return `M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`;
      } else {
        const midX = startX + (endX - startX) / 2;
        return `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
      }
    } else {
      if (isVertical) {
        const distance = Math.abs(endY - startY);
        const elbowY = startY + distance * 0.6 * Math.sign(endY - startY);
        return `M ${startX} ${startY} L ${startX} ${elbowY} L ${endX} ${elbowY} L ${endX} ${endY}`;
      } else {
        const distance = Math.abs(endX - startX);
        const elbowX = startX + distance * 0.6 * Math.sign(endX - startX);
        return `M ${startX} ${startY} L ${elbowX} ${startY} L ${elbowX} ${endY} L ${endX} ${endY}`;
      }
    }
  }

  private toggle(): void {
    if (this.isVisible()) {
      this.hide();
    } else {
      void this.show();
    }
  }

  private scheduleHide(): void {
    this.clearHideTimeout();
    this.hideTimeout = setTimeout(() => {
      this.hide();
    }, 100);
  }

  private clearHideTimeout(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }
  }
}
